// API Service - Multi-user, Multi-company Finance Tracker
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Token management
const getToken = () => localStorage.getItem('authToken');
const setToken = (token) => localStorage.setItem('authToken', token);
const removeToken = () => localStorage.removeItem('authToken');

// User management
const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};
const setUser = (user) => localStorage.setItem('user', JSON.stringify(user));
const removeUser = () => localStorage.removeItem('user');

const handleResponse = async (response) => {
  if (response.status === 401) {
    // Token expired or invalid
    removeToken();
    removeUser();
    window.location.href = '/login';
    throw new Error('Session expired. Please login again.');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  return response.json();
};

const authHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// ==================== AUTH ====================

export const login = async (phone, pin) => {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, pin })
  });
  const data = await handleResponse(response);
  setToken(data.token);
  setUser(data.user);
  return data;
};

export const logout = () => {
  removeToken();
  removeUser();
};

export const isAuthenticated = () => {
  return !!getToken() && !!getUser();
};

export const getCurrentUser = () => getUser();

export const changePin = async (oldPin, newPin) => {
  const response = await fetch(`${API_URL}/api/auth/change-pin`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ old_pin: oldPin, new_pin: newPin })
  });
  return handleResponse(response);
};

export const getMe = async () => {
  const response = await fetch(`${API_URL}/api/auth/me`, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const switchCompany = async (companyId) => {
  const response = await fetch(`${API_URL}/api/auth/switch-company`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ company_id: companyId })
  });
  const data = await handleResponse(response);
  // Update token and user in localStorage
  setToken(data.token);
  setUser(data.user);
  return data;
};

// ==================== SETUP ====================

export const initializeSystem = async () => {
  const response = await fetch(`${API_URL}/api/setup/init`, {
    method: 'POST'
  });
  return handleResponse(response);
};

// ==================== USERS (Super Admin) ====================

export const getUsers = async () => {
  const response = await fetch(`${API_URL}/api/users`, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const createUser = async (userData) => {
  const response = await fetch(`${API_URL}/api/users`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(userData)
  });
  return handleResponse(response);
};

export const updateUser = async (id, updates) => {
  const response = await fetch(`${API_URL}/api/users/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(updates)
  });
  return handleResponse(response);
};

export const deleteUser = async (id) => {
  const response = await fetch(`${API_URL}/api/users/${id}`, {
    method: 'DELETE',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const resetUserPin = async (id) => {
  const response = await fetch(`${API_URL}/api/users/${id}/reset-pin`, {
    method: 'POST',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// ==================== COMPANIES (Super Admin) ====================

export const getCompanies = async () => {
  const response = await fetch(`${API_URL}/api/companies`, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const createCompany = async (companyData) => {
  const response = await fetch(`${API_URL}/api/companies`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(companyData)
  });
  return handleResponse(response);
};

export const updateCompany = async (id, updates) => {
  const response = await fetch(`${API_URL}/api/companies/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(updates)
  });
  return handleResponse(response);
};

export const deleteCompany = async (id) => {
  const response = await fetch(`${API_URL}/api/companies/${id}`, {
    method: 'DELETE',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// ==================== WALLETS ====================

export const getWallets = async () => {
  const response = await fetch(`${API_URL}/api/wallets`, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const getWalletStatement = async (id, startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const url = params.toString()
    ? `${API_URL}/api/wallets/${id}/statement?${params.toString()}`
    : `${API_URL}/api/wallets/${id}/statement`;
  const response = await fetch(url, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const addWallet = async (walletData) => {
  const response = await fetch(`${API_URL}/api/wallets`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(walletData)
  });
  return handleResponse(response);
};

export const updateWallet = async (id, updates) => {
  const response = await fetch(`${API_URL}/api/wallets/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(updates)
  });
  return handleResponse(response);
};

export const deleteWallet = async (id, reason) => {
  const response = await fetch(`${API_URL}/api/wallets/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify({ reason })
  });
  return handleResponse(response);
};

export const transferBetweenWallets = async (fromId, toId, amount, date = null) => {
  const response = await fetch(`${API_URL}/api/wallets/transfer`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      from_wallet_id: fromId,
      to_wallet_id: toId,
      amount,
      date: date || null
    })
  });
  return handleResponse(response);
};

// ==================== CATEGORIES ====================

export const getCategories = async () => {
  const response = await fetch(`${API_URL}/api/categories`, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const addCategory = async (categoryData) => {
  const response = await fetch(`${API_URL}/api/categories`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(categoryData)
  });
  return handleResponse(response);
};

export const updateCategory = async (id, updates) => {
  const response = await fetch(`${API_URL}/api/categories/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(updates)
  });
  return handleResponse(response);
};

export const deleteCategory = async (id, reason) => {
  const response = await fetch(`${API_URL}/api/categories/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify({ reason })
  });
  return handleResponse(response);
};

// ==================== TRANSACTIONS ====================

export const getTransactions = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.type) params.append('type', filters.type);
  if (filters.wallet_id) params.append('wallet_id', filters.wallet_id);
  if (filters.category_id) params.append('category_id', filters.category_id);
  if (filters.category_name) params.append('category_name', filters.category_name);
  
  const url = params.toString() 
    ? `${API_URL}/api/transactions?${params.toString()}`
    : `${API_URL}/api/transactions`;
  
  const response = await fetch(url, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const addCashIn = async (walletId, amount, source, narration, date = null) => {
  const response = await fetch(`${API_URL}/api/transactions/cash-in`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      wallet_id: walletId,
      amount,
      source,
      narration,
      date: date || null
    })
  });
  return handleResponse(response);
};

export const addCashOut = async (walletId, categoryId, amount, phoneNumber, narration, date = null) => {
  const response = await fetch(`${API_URL}/api/transactions/cash-out`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      wallet_id: walletId,
      category_id: categoryId,
      amount,
      phone_number: phoneNumber,
      narration,
      date: date || null
    })
  });
  return handleResponse(response);
};

export const updateTransaction = async (id, updates) => {
  const response = await fetch(`${API_URL}/api/transactions/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(updates)
  });
  return handleResponse(response);
};

export const deleteTransaction = async (id, reason) => {
  const response = await fetch(`${API_URL}/api/transactions/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify({ reason })
  });
  return handleResponse(response);
};

// ==================== REPORTS ====================

export const getReportSummary = async (startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  
  const url = params.toString()
    ? `${API_URL}/api/reports/summary?${params.toString()}`
    : `${API_URL}/api/reports/summary`;
  
  const response = await fetch(url, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

// ==================== DATA EXPORT/IMPORT ====================

export const exportData = async () => {
  const response = await fetch(`${API_URL}/api/data/export`, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const importData = async (data) => {
  const response = await fetch(`${API_URL}/api/data/import`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

// ==================== AUDIT LOGS (Super Admin) ====================

export const getAuditLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.company_id) params.append('company_id', filters.company_id);
  if (filters.entity_type) params.append('entity_type', filters.entity_type);
  if (filters.action) params.append('action', filters.action);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  
  const url = params.toString()
    ? `${API_URL}/api/audit-logs?${params.toString()}`
    : `${API_URL}/api/audit-logs`;
  
  const response = await fetch(url, {
    headers: authHeaders()
  });
  return handleResponse(response);
};

// ==================== LEGACY COMPATIBILITY ====================
// These are kept for backward compatibility during migration

export const verifyPin = async (pin) => {
  const response = await fetch(`${API_URL}/api/auth/verify-pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin })
  });
  return handleResponse(response);
};

export const isPinVerified = () => isAuthenticated();
export const setPinVerified = () => {}; // No-op for compatibility
